import type { ForwardDealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import {
  getPriceSourceFixingsByFullSymbolFromMapping,
  USD_PEGGED_NO_CONVERSION_INDEX,
  type FixingIndexMappingMap,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import type { WritableDraft } from 'immer';

/**
 * Forward OTC field derivation: inverse payoff delivery and index selection.
 * Used by Redux (`ForwardProductBuilderSliceStrategy.updateOTCFields`) and the OTC fields dialog local state.
 */
export type ForwardOtcFieldsUpdateTarget = ForwardDealProductBuilderRow | WritableDraft<ForwardDealProductBuilderRow>;

export function applyForwardOtcFieldsUpdate(
  state: ForwardOtcFieldsUpdateTarget,
  otcFields: ForwardDealProductBuilderRow['otcFields'],
  fixingIndexMapping?: FixingIndexMappingMap
): void {
  if (otcFields == null) {
    state.otcFields = otcFields;
    return;
  }
  const currentPayoffType = state.otcFields?.payoffType;
  const newPayoffType = otcFields?.payoffType;
  const currentDeliveryAsset = state.otcFields?.deliveryAsset;
  const currentQuoteAsset = state.otcFields?.quoteAsset;
  const newQuoteAsset = otcFields?.quoteAsset;
  const currentIndex = state.otcFields?.index;
  const isPayoffTypeChanging = currentPayoffType !== newPayoffType;
  const isQuoteAssetChanging = currentQuoteAsset !== newQuoteAsset;

  // Shallow-copy payload into draft state. Action payloads can be frozen (e.g. RTK); assigning the
  // same reference then mutating deliveryAsset/index below throws in strict mode.
  state.otcFields = {
    ...otcFields,
    ...(!otcFields.deliveryAsset && currentDeliveryAsset ? { deliveryAsset: currentDeliveryAsset } : {}),
  };

  if (isPayoffTypeChanging) {
    if (newPayoffType === 'Inverse') {
      state.otcFields.deliveryAsset = state.baseAsset || undefined;
    } else if (newPayoffType === 'Linear' && currentPayoffType === 'Inverse') {
      state.otcFields.deliveryAsset = undefined;
    }
  } else if (newPayoffType === 'Inverse' && !state.otcFields.deliveryAsset && state.baseAsset) {
    state.otcFields.deliveryAsset = state.baseAsset;
  }

  if (state.baseAsset && newQuoteAsset && fixingIndexMapping) {
    const normalizedQuoteAsset = newQuoteAsset === 'USDT' || newQuoteAsset === 'USDC' ? 'USD' : newQuoteAsset;
    const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
      state.baseAsset,
      normalizedQuoteAsset,
      fixingIndexMapping
    );
    const availableIndexes = fixings
      .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
      .map(fixing => fixing.Name);

    if (availableIndexes.length === 1) {
      state.otcFields.index = availableIndexes[0];
    } else if (isQuoteAssetChanging && currentIndex && availableIndexes.length > 0) {
      const currentIndexIsValid = availableIndexes.includes(currentIndex);
      if (!currentIndexIsValid) {
        state.otcFields.index = availableIndexes[0];
      }
    }
  }
}
